import type { Signal } from '@angular/core';
import type { ListFilterOperator } from '@zag/core/common';

export type { ListFilterOperator } from '@zag/core/common';

/**
 * Pagination metadata for list responses.
 * NOTE: This matches the canonical schema in @zag/core/models.
 * Duplicated here because ng-packagr cannot resolve cross-library references during build.
 */
export interface ListMeta {
  /** Number of items requested */
  take: number;
  /** Number of items to skip */
  skip: number;
  /** Number of items returned in this response */
  count: number;
  /** Total number of items available */
  total: number;
  /** Whether there are more items to fetch */
  hasMore: boolean;
}

/**
 * Standard list response format.
 * NOTE: This matches the canonical schema in @zag/core/models.
 * Duplicated here because ng-packagr cannot resolve cross-library references during build.
 */
export interface ListResponse<T> {
  data: T[];
  meta: ListMeta;
}

export type QueryPrimitive = string | number | boolean;

export type QueryParamValue = QueryPrimitive | QueryPrimitive[];

export interface ListSort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ListFilter {
  field: string;
  operator: ListFilterOperator;
  value?: QueryPrimitive | QueryPrimitive[];
}

export interface ListSearch {
  field: string;
  term: string;
}

/**
 * Options for creating a list resource instance
 */
export interface ListResourceOptions {
  /** Initial window size (default: 50) */
  take?: number;
  /** Initial window offset (default: 0) */
  skip?: number;
  /** Initial sort clauses */
  sort?: ListSort[];
  /** Initial filter clauses */
  filter?: ListFilter[];
  /** Initial search clauses */
  search?: ListSearch[];
  /** Additional endpoint-specific query parameters */
  params?: Record<string, QueryParamValue>;
}

/**
 * Options for creating an item resource instance
 */
export interface ItemResourceOptions {
  /** ID signal to reactively fetch by */
  id: Signal<string | null>;
}

/**
 * Resource state interface for list resources.
 * Each instance is independent with its own filter state.
 */
export interface ListResource<T> {
  /** The items array */
  readonly value: Signal<T[]>;
  /** Full list response metadata */
  readonly meta: Signal<ListMeta>;
  /** Total count from pagination */
  readonly total: Signal<number>;
  /** Loaded row count in current window */
  readonly count: Signal<number>;
  /** Whether additional data can be loaded */
  readonly hasMore: Signal<boolean>;
  /** Whether currently loading */
  readonly isLoading: Signal<boolean>;
  /** Any error from last request */
  readonly error: Signal<Error | undefined>;
  /** Whether initial load is complete */
  readonly isLoaded: Signal<boolean>;
  /** Current take value */
  readonly take: Signal<number>;
  /** Current skip value */
  readonly skip: Signal<number>;
  /** Current sort clauses */
  readonly sort: Signal<ListSort[]>;
  /** Current filter clauses */
  readonly filter: Signal<ListFilter[]>;
  /** Current search clauses */
  readonly search: Signal<ListSearch[]>;
  /** Additional endpoint-specific query params */
  readonly params: Signal<Record<string, QueryParamValue>>;
  /** Set take window size */
  setTake(take: number): void;
  /** Set skip offset */
  setSkip(skip: number): void;
  /** Set both skip and take in one update */
  setWindow(skip: number, take: number): void;
  /** Replace sort clauses */
  setSort(sort: ListSort[]): void;
  /** Replace filter clauses (resets skip to 0) */
  setFilter(filter: ListFilter[]): void;
  /** Replace search clauses (resets skip to 0) */
  setSearch(search: ListSearch[]): void;
  /** Patch additional endpoint-specific query params */
  patchParams(params: Record<string, QueryParamValue | undefined>): void;
  /** Trigger a reload */
  reload(): void;
}

/**
 * Resource state interface for single-item resources (reactive fetch).
 * Each instance is independent.
 */
export interface ItemResource<T> {
  /** The item (null if not loaded/not found) */
  readonly value: Signal<T | null>;
  /** Whether currently loading */
  readonly isLoading: Signal<boolean>;
  /** Any error from last request */
  readonly error: Signal<Error | undefined>;
  /** Whether load is complete */
  readonly isLoaded: Signal<boolean>;
  /** Trigger a reload */
  reload(): void;
}

/**
 * Resource state interface for one-time fetch operations.
 * Provides signal-based state tracking for a single request.
 */
export interface FetchResource<T> {
  /** The fetched item (null until loaded) */
  readonly value: Signal<T | null>;
  /** Whether currently loading */
  readonly isLoading: Signal<boolean>;
  /** Any error from the request */
  readonly error: Signal<Error | undefined>;
  /** Whether the fetch is complete (success or error) */
  readonly isLoaded: Signal<boolean>;
}

/**
 * Resource state interface for mutation operations (create, update, delete).
 * Provides signal-based state tracking for the mutation.
 *
 * @example
 * ```typescript
 * const mutation = this.api.skills.create({ name: 'TypeScript' });
 *
 * // In template
 * @if (mutation.isLoading()) {
 *   <spinner />
 * } @else if (mutation.error()) {
 *   <error-message [error]="mutation.error()" />
 * } @else if (mutation.value()) {
 *   <success-message>Created: {{ mutation.value()!.name }}</success-message>
 * }
 * ```
 */
export interface MutationResource<T> {
  /** The result of the mutation (null until complete) */
  readonly value: Signal<T | null>;
  /** Whether the mutation is in progress */
  readonly isLoading: Signal<boolean>;
  /** Any error from the mutation */
  readonly error: Signal<Error | undefined>;
  /** Whether the mutation is complete (success or error) */
  readonly isComplete: Signal<boolean>;
  /** Whether the mutation succeeded */
  readonly isSuccess: Signal<boolean>;
}

/**
 * Factory interface for creating domain resource instances.
 *
 * Each call creates an independent resource with its own state,
 * ensuring no interference between different consumers.
 *
 * All methods return resource objects with signal-based state tracking,
 * providing a unified API for handling loading, error, and success states.
 *
 * @example
 * ```typescript
 * // List resource
 * readonly skills = this.api.skills();
 * // Template: @if (skills.isLoading()) { ... }
 *
 * // Reactive item resource
 * readonly skill = this.api.skills({ id: this.skillId });
 * // Template: @if (skill.isLoading()) { ... }
 *
 * // One-time fetch
 * readonly fetchedSkill = this.api.skills.get('skill-123');
 * // Template: @if (fetchedSkill.isLoading()) { ... }
 *
 * // Mutations
 * onSave() {
 *   this.saveResult = this.api.skills.create({ name: 'New Skill' });
 * }
 * // Template: @if (saveResult?.isLoading()) { ... }
 * ```
 */
export interface ResourceFactory<T, TCreate = Partial<T>, TUpdate = Partial<T>> {
  /**
   * Create a new list resource instance with independent state.
   * @param options Optional initial configuration
   */
  (options?: ListResourceOptions): ListResource<T>;

  /**
   * Create a new item resource instance for fetching by ID (reactive).
   * @param options Configuration with ID signal
   */
  (options: ItemResourceOptions): ItemResource<T>;

  /**
   * Fetch a single item by ID (one-time fetch).
   * Returns a resource with signal-based state tracking.
   *
   * @example
   * ```typescript
   * readonly skill = this.api.skills.get('skill-123');
   *
   * // In template
   * @if (skill.isLoading()) {
   *   <spinner />
   * } @else if (skill.value(); as s) {
   *   <div>{{ s.name }}</div>
   * }
   * ```
   */
  get(id: string): FetchResource<T>;

  /**
   * Create a new item (POST request).
   * Returns a mutation resource with signal-based state tracking.
   *
   * @example
   * ```typescript
   * onSubmit() {
   *   this.createResult = this.api.skills.create({ name: 'New Skill' });
   * }
   *
   * // In template
   * @if (createResult?.isLoading()) {
   *   <button disabled>Saving...</button>
   * }
   * ```
   */
  create(data: TCreate): MutationResource<T>;

  /**
   * Update an existing item (PATCH request).
   * Returns a mutation resource with signal-based state tracking.
   */
  update(id: string, data: TUpdate): MutationResource<T>;

  /**
   * Delete an item (DELETE request).
   * Returns a mutation resource with signal-based state tracking.
   */
  delete(id: string): MutationResource<void>;
}
